import {
  createPasskey,
  passkeysSupported,
  type StaffMe,
  type TotpEnrollBeginResponse,
  type TrustedBrowser,
} from '@central/api';
import {
  Card,
  StatusChip,
  TotpQr,
  color,
  describeUserAgent,
  formatAgo,
  RecoveryCodeList,
  formatDate,
  shownError,
  useReauth,
  type Guard,
  InfoRow,
  ChangePasswordDialog,
} from '@central/ui';
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { keys, meQuery, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { mono, muted } from '../lib/labels';
import { Empty, QueryState } from './parts';

const RECOVERY_SET = 10;

/** The signed-in staff user's StaffMe, kept current as factors change. */
function useLiveMe(initial: StaffMe): StaffMe {
  const api = useApi();
  return useQuery({ ...meQuery(api), initialData: initial }).data;
}

function ErrorAlert({ error }: { error: unknown }) {
  const e = shownError(error);
  return e ? <Alert color="red">{errorMessage(e)}</Alert> : null;
}

/**
 * "Your sign-in": password, passkeys, authenticator app and recovery codes of
 * the signed-in user. Changing a factor needs a fresh second factor
 * (useReauth).
 */
export function YourSignIn({ me: initial }: { me: StaffMe }) {
  const api = useApi();
  const qc = useQueryClient();
  const me = useLiveMe(initial);
  const f = me.user.secondFactors;
  const { guard, prompt } = useReauth(f, {
    api: api.auth,
    describeError: errorMessage,
    buttonColor: 'dark',
  });
  const [panel, setPanel] = useState<'password' | 'passkey' | 'totp' | 'codes'>();
  const [codes, setCodes] = useState<string[]>();

  const passkeys = useQuery({
    queryKey: keys.credentials,
    queryFn: () => api.auth.listCredentials(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => guard(() => api.auth.deleteCredential(id)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.credentials });
      await qc.invalidateQueries({ queryKey: keys.me });
      await qc.invalidateQueries({ queryKey: keys.trustedBrowsers });
    },
  });
  const generate = useMutation({
    mutationFn: () => guard(() => api.auth.generateRecoveryCodes()),
    onSuccess: async (r) => {
      setCodes(r.codes);
      setPanel('codes');
      await qc.invalidateQueries({ queryKey: keys.me });
    },
  });

  const enrolled = async (next: StaffMe) => {
    qc.setQueryData(keys.me, next);
    setPanel(undefined);
    await qc.invalidateQueries({ queryKey: keys.credentials });
  };
  const hasFactor = f.totp || f.passkeys > 0;

  return (
    <Card title="Your sign-in">
      <InfoRow label="Email">{me.user.email}</InfoRow>
      <InfoRow label="Password">
        <Button variant="subtle" size="compact-sm" onClick={() => setPanel('password')}>
          Change
        </Button>
      </InfoRow>
      <InfoRow label="Authenticator app">
        <Group gap={8}>
          {f.totp ? (
            <StatusChip tone="ok">On</StatusChip>
          ) : (
            <>
              <StatusChip tone="unk">Off</StatusChip>
              <Button variant="subtle" size="compact-sm" onClick={() => setPanel('totp')}>
                Set up
              </Button>
            </>
          )}
        </Group>
      </InfoRow>
      <InfoRow label="Recovery codes">
        <Group gap={8}>
          <span>
            {f.recoveryCodes} of {RECOVERY_SET} left
          </span>
          <Button
            variant="subtle"
            size="compact-sm"
            disabled={!hasFactor}
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {f.recoveryCodes > 0 ? 'Regenerate' : 'Create'}
          </Button>
        </Group>
      </InfoRow>
      <ErrorAlert error={generate.error} />
      <div style={{ padding: '12px 0 4px' }}>
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Passkeys
          </Text>
          {passkeysSupported() && (
            <Button variant="subtle" size="compact-sm" onClick={() => setPanel('passkey')}>
              Add a passkey
            </Button>
          )}
        </Group>
        <QueryState isPending={passkeys.isPending} error={passkeys.error}>
          {() =>
            passkeys.data!.items.length === 0 ? (
              <Empty>No passkeys.</Empty>
            ) : (
              <ul aria-label="Passkeys" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {passkeys.data!.items.map((p) => (
                  <ListRow
                    key={p.id}
                    main={p.name}
                    detail={`Added ${formatDate(p.createdAt)} · ${
                      p.lastUsedAt ? `last used ${formatAgo(p.lastUsedAt)}` : 'never used'
                    }`}
                    action={
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-sm"
                        aria-label={`Remove ${p.name}`}
                        loading={remove.isPending && remove.variables === p.id}
                        onClick={() => remove.mutate(p.id)}
                      >
                        Remove
                      </Button>
                    }
                  />
                ))}
              </ul>
            )
          }
        </QueryState>
        <ErrorAlert error={remove.error} />
      </div>
      {prompt}
      <ChangePasswordDialog
        opened={panel === 'password'}
        guard={guard}
        onClose={() => setPanel(undefined)}
        changePassword={(b) => api.auth.changePassword(b)}
        // The server forgets every trusted browser in the same change.
        onChanged={() => qc.invalidateQueries({ queryKey: keys.trustedBrowsers })}
        dark
      />
      <Modal opened={panel === 'passkey'} onClose={() => setPanel(undefined)} title="Add a passkey">
        <AddPasskey guard={guard} onDone={enrolled} />
      </Modal>
      <Modal
        opened={panel === 'totp'}
        onClose={() => setPanel(undefined)}
        title="Set up an authenticator app"
      >
        <AddTotp guard={guard} onDone={enrolled} />
      </Modal>
      <Modal
        opened={panel === 'codes' && !!codes}
        onClose={() => {
          setCodes(undefined);
          setPanel(undefined);
        }}
        title="Your new recovery codes"
      >
        <Stack gap={12}>
          <Text size="sm">
            Each code signs you in once. They are shown only once, and your earlier codes no longer
            work.
          </Text>
          {codes && <RecoveryCodeList codes={codes} />}
        </Stack>
      </Modal>
    </Card>
  );
}

function AddPasskey({ guard, onDone }: { guard: Guard; onDone: (me: StaffMe) => void }) {
  const api = useApi();
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () =>
      guard(async () => {
        const { publicKey } = await api.auth.webauthnRegisterBegin();
        const credential = await createPasskey(publicKey);
        return api.auth.webauthnRegisterFinish({ credential, name: name.trim() || 'Passkey' });
      }),
    onSuccess: onDone,
  });
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <Stack gap={14}>
        <TextInput
          label="Name"
          placeholder="e.g. Work laptop"
          data-autofocus
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <ErrorAlert error={add.error} />
        <Group justify="flex-end">
          <Button type="submit" color="dark" loading={add.isPending}>
            Create passkey
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

function AddTotp({ guard, onDone }: { guard: Guard; onDone: (me: StaffMe) => void }) {
  const api = useApi();
  const [secret, setSecret] = useState<TotpEnrollBeginResponse>();
  const [code, setCode] = useState('');
  const begin = useMutation({
    mutationFn: () => guard(() => api.auth.totpEnrollBegin()),
    onSuccess: setSecret,
  });
  const finish = useMutation({
    mutationFn: () => guard(() => api.auth.totpEnrollFinish({ code: code.replace(/\s/g, '') })),
    onSuccess: onDone,
    onSettled: () => setCode(''),
  });
  if (!secret) {
    return (
      <Stack gap={14}>
        <Text size="sm">
          You will scan a QR code with an app such as Microsoft Authenticator or 1Password.
        </Text>
        <ErrorAlert error={begin.error} />
        <Group justify="flex-end">
          <Button color="dark" loading={begin.isPending} onClick={() => begin.mutate()}>
            Continue
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        finish.mutate();
      }}
    >
      <Stack gap={14}>
        <Text size="sm">Scan this code, or enter the key by hand. It is shown only once.</Text>
        <div style={{ alignSelf: 'center' }}>
          <TotpQr value={secret.otpauthUrl} />
        </div>
        <code data-testid="totp-secret" style={{ ...mono, wordBreak: 'break-all' }}>
          {secret.secret}
        </code>
        <TextInput
          label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />
        <ErrorAlert error={finish.error} />
        <Group justify="flex-end">
          <Button type="submit" color="dark" loading={finish.isPending}>
            Turn on
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/** Browsers the user trusted to skip the second step, with Forget. */
export function TrustedBrowsers() {
  const api = useApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: keys.trustedBrowsers,
    queryFn: () => api.auth.trustedBrowsers(),
  });
  const forget = useMutation({
    mutationFn: (id: string) => api.auth.revokeTrustedBrowser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.trustedBrowsers }),
  });
  return (
    <Card title="Trusted browsers">
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          list.data!.items.length === 0 ? (
            <Empty>
              None. Tick “Trust this browser for 7 days” when you sign in to skip the second step on
              that browser.
            </Empty>
          ) : (
            <ul aria-label="Trusted browsers" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {list.data!.items.map((b) => (
                <BrowserRow
                  key={b.id}
                  b={b}
                  busy={forget.isPending && forget.variables === b.id}
                  onForget={() => forget.mutate(b.id)}
                />
              ))}
            </ul>
          )
        }
      </QueryState>
      <ErrorAlert error={forget.error} />
    </Card>
  );
}

function BrowserRow({
  b,
  busy,
  onForget,
}: {
  b: TrustedBrowser;
  busy: boolean;
  onForget: () => void;
}) {
  const name = describeUserAgent(b.userAgent);
  return (
    <ListRow
      main={
        <Group gap={8}>
          <span>{name}</span>
          {b.current && <StatusChip tone="ok">This browser</StatusChip>}
        </Group>
      }
      detail={[
        b.ip,
        `trusted ${formatDate(b.createdAt)}`,
        b.lastUsedAt ? `last used ${formatAgo(b.lastUsedAt)}` : undefined,
        `until ${formatDate(b.expiresAt)}`,
      ]
        .filter(Boolean)
        .join(' · ')}
      action={
        <Button
          variant="subtle"
          color="red"
          size="compact-sm"
          aria-label={`Forget ${name}${b.current ? ' (this browser)' : ''}`}
          loading={busy}
          onClick={onForget}
        >
          Forget
        </Button>
      }
    />
  );
}

function ListRow({ main, detail, action }: { main: ReactNode; detail: string; action: ReactNode }) {
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '9px 0',
        borderBottom: `1px solid ${color.rowRule}`,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 14 }}>{main}</span>
        <span style={{ fontSize: 13, ...muted }}>{detail}</span>
      </div>
      {action}
    </li>
  );
}
